import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useContext,
} from "react";
import { Formik } from "formik";
import { connect, ConnectedProps } from "react-redux";
import { useTranslation } from "react-i18next";
import { matchPath, useLocation } from "react-router-dom";

import { $Avatar, $Typography, $Button, $Dropdown } from "us.common/components";
import "./Assistant.scss";
import {
  MessageInput,
  ChatBubble,
  WelcomeScreen,
  DateSeparator,
  SettingsPopover,
} from "./components";
import { UniQChatMessage } from "us.common/interfaces";
import { RootState } from "us.helper/types";
import { uniQActions } from "us.common/actions/UniQ";
import { MessageStorage } from "./utils/MessageStorage";
import { useAuth } from "us.helper/hooks/useAuth";
import {
  groupMessagesByDate,
  buildCaseSummaryMessage,
  hasCaseSummary,
} from "./functions";
import {
  RobotOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
  SettingOutlined,
} from "us.icons";
import { UniQContext } from "../../UniQ";
import { URLType } from "us.common/constants";

/**
 * Renders the UniQ Assistant chat interface component.
 *
 * This component provides a conversational UI for users to interact with an assistant.
 * It manages chat messages, handles user input, and simulates assistant responses.
 *
 * Features:
 * - Displays a chat header with user greeting.
 * - Shows chat messages between the user and the assistant.
 * - Handles message sending and simulates API responses.
 * - Displays a loading indicator while awaiting assistant response.
 * - Integrates with Formik for message input form management.
 * - Resets messages when onReset prop is triggered.
 * - Persists messages in localStorage until user session expires (new access token).
 * - Auto-loads user-specific messages from localStorage on component mount.
 * - Handles user session changes and loads appropriate message history.
 *
 * @component
 * @returns {JSX.Element} The rendered Assistant chat interface.
 *
 * @example
 * ```tsx
 * <Assistant onReset={handleReset} />
 * ```
 */
const Assistant: React.FC<PropsFromRedux & { onReset?: boolean }> = (
  props,
): JSX.Element => {
  const { account } = useAuth();
  const { t } = useTranslation("US.COMMON");
  const { isOpen } = useContext(UniQContext);
  const {
    defaultPromptData,
    isFetchingDefaultPrompt,
    newMessage,
    sessionReset,
    sendMessage,
    resetMessage,
    clearMessages,
    onReset,
    openSettings,
    getDefaultPrompt,
    resetDefaultPromptData,
    resetSession,
  } = props;
  const location = useLocation();
  const pathname = location.pathname;

  const { params }: any = matchPath(
    {
      path: "/:type/:id?/:module?",
      end: false,
      caseSensitive: false,
    },
    pathname,
  ) ?? { params: {} };
  const { id: caseNo, type } = params ?? {};

  const { caseFullDetails, aiGeneratedSummary, additionalMessages } =
    defaultPromptData ?? {};

  // Local state for messages with persistence
  const [messages, setMessages] = useState<UniQChatMessage[]>(() => {
    // Initialize messages from storage on component mount
    const userId = account?.homeAccountId || account?.username;
    const storedMessages = MessageStorage.loadMessages(userId);
    return storedMessages.length > 0 ? storedMessages : [];
  });

  // Loading status state for enhanced feedback
  const [loadingStatus, setLoadingStatus] = useState<string>("");
  const loadingTimerRef = useRef<NodeJS.Timeout | null>(null);

  const lastMessageCountRef = useRef(0);
  const currentUserIdRef = useRef(account?.homeAccountId || account?.username);
  const summaryScrollTargetRef = useRef<string | null>(null);
  const isSyncingFromStorageRef = useRef(false);

  // 1. Handle user changes and onReset (critical state changes only)
  useEffect(() => {
    const currentUserId = account?.homeAccountId || account?.username;

    // Handle reset
    if (onReset) {
      setMessages([]);
      MessageStorage.clearMessages(currentUserId);
      resetMessage && resetMessage({});
      clearMessages && clearMessages([]);
      lastMessageCountRef.current = 0;
      return;
    }

    // Handle user change
    if (currentUserId !== currentUserIdRef.current) {
      currentUserIdRef.current = currentUserId;
      const storedMessages = MessageStorage.loadMessages(currentUserId);
      setMessages(storedMessages.length > 0 ? storedMessages : []);
      lastMessageCountRef.current = storedMessages.length;
    }
  }, [
    account?.homeAccountId,
    account?.username,
    onReset,
    resetMessage,
    clearMessages,
  ]);

  // 2. Handle new assistant responses (only when new response arrives)
  useEffect(() => {
    if (newMessage.data && !newMessage.isLoading) {
      setMessages((prev) => {
        const messageExists = prev.some((msg) => msg.id === newMessage.data.id);
        if (!messageExists) {
          return [...prev, newMessage.data];
        }
        return prev;
      });
    }
  }, [newMessage.data, newMessage.isLoading]);

  // 3. Manage dynamic loading status messages
  useEffect(() => {
    if (newMessage.isLoading) {
      // Start loading
      setLoadingStatus(t("US.COMMON:UNIQ.LOADING_MESSAGE"));

      // Clear any existing timer
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }

      // Set timer for longer processing message
      loadingTimerRef.current = setTimeout(() => {
        setLoadingStatus(t("US.COMMON:UNIQ.LOADING_COMPLEX_MESSAGE"));
      }, 8000); // Show after 8 seconds
    } else {
      // Stop loading
      setLoadingStatus("");

      // Clear timer
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    }

    // Cleanup function
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    };
  }, [newMessage.isLoading]);

  // 4. Handle default prompt loading for case views
  useEffect(() => {
    if (isOpen && type == URLType.CASE) {
      if (!hasCaseSummary(messages, caseNo)) {
        getDefaultPrompt && getDefaultPrompt({ caseNo });
      }
    }
  }, [caseNo, type, isOpen, messages]);

  // 5. Add default prompt response to chat when it arrives
  useEffect(() => {
    if (isOpen && type == URLType.CASE) {
      if (
        caseFullDetails?.caseOverview?.caseNumber == caseNo &&
        !hasCaseSummary(messages, caseNo)
      ) {
        const caseSummaryMessage = buildCaseSummaryMessage(
          caseNo,
          aiGeneratedSummary,
          additionalMessages,
        );
        if (caseSummaryMessage) {
          summaryScrollTargetRef.current = caseSummaryMessage.id;
          setMessages((prev) => [...prev, caseSummaryMessage]);
        }
      }
    }
  }, [
    defaultPromptData.aiGeneratedSummary,
    caseNo,
    type,
    isOpen,
    messages.length,
  ]);

  const handleSubmit = useCallback(
    (values: any) => {
      const message = values.message.trim();
      if (message) {
        // Add user message to local state
        const userMessage: UniQChatMessage = {
          id: Date.now().toString(),
          responseType: "TEXT",
          responseContent: message,
          links: [],
          timestamp: new Date(),
          isUser: true,
        };

        setMessages((prev) => [...prev, userMessage]);

        // Send to Redux for API call - send as UniQCommand format
        sendMessage && sendMessage(userMessage);
      }
    },
    [sendMessage],
  );

  // Handle info message display
  const handleShowInfo = useCallback(() => {
    const infoMessage: UniQChatMessage = {
      id: `info-${Date.now()}`,
      responseType: "TEXT",
      responseContent: `**UniQ Assistant Information**

**Version:** 1.0.0

**Features:**
• Inquiry-based responses using available data in UnicornBOX, without requiring navigation to specific screens
• Context-aware replies that reference relevant historical interactions
• Option to refresh the chat and start from the beginning
• Ability to reference chat history in user interface for up to a couple of hours

**Supported Topics/Domains:**
• Case basic details
• Case history details
• Debtor based details
• Creditor based details
• Message drafting capability according to the context and system details

**Note worthy limitations:**
• Responses are AI-generated and may not always be fully accurate
• Occasional inaccuracies may occur due to timeouts or system capacity constraints
• Functionality is limited to basic case details and history; it does not support in-depth event analysis or predictive insights
• Cannot perform actions or changes based on user inquiries
• Should not be relied upon for critical decisions without manual verification`,
      links: [],
      timestamp: new Date(),
      isUser: false,
    };
    setMessages((prev) => [...prev, infoMessage]);
  }, []);

  // Handle default prompt modal
  const handleSettingsPopover = () => {
    openSettings && openSettings({});
  };

  // Handle manual refresh of case summary
  const handleRefreshSummary = () => {
    resetDefaultPromptData && resetDefaultPromptData({});
    setTimeout(() => {
      setMessages((prev) =>
        prev.filter((msg) => !msg.id.includes(`case-sum-${caseNo}`)),
      );
    }, 100);
  };

  // Scroll to bottom when messages change (using a simple effect)
  const scrollToBottom = useCallback(() => {
    const chatContainer = document.querySelector(".chat-messages");
    if (chatContainer) {
      setTimeout(() => {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }, 0);
    }
  }, []);

  // Call scroll after render when messages change
  useEffect(() => {
    if (messages.length !== lastMessageCountRef.current) {
      const summaryId = summaryScrollTargetRef.current;
      if (summaryId) {
        summaryScrollTargetRef.current = null;
        setTimeout(() => {
          const el = document.getElementById(`msg-${summaryId}`);
          el?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);
      } else {
        scrollToBottom();
      }
      lastMessageCountRef.current = messages.length;
    }
  }, [messages.length, scrollToBottom]);

  // Save messages to storage when they change (separate from UI updates)
  useEffect(() => {
    if (isSyncingFromStorageRef.current) {
      isSyncingFromStorageRef.current = false;
      return;
    }

    if (messages.length > 0) {
      const userId = account?.homeAccountId || account?.username;
      MessageStorage.saveMessages(messages, userId);
    }
  }, [messages, account?.homeAccountId, account?.username]);

  const handleStartOver = () => {
    if (messages.length > 0) {
      resetSession && resetSession({});
      const messagesWithoutInitialInfo = messages.filter(
        (msg) => !msg.id.startsWith("info-"),
      );
      if (messagesWithoutInitialInfo.length === 0) {
        // If only the info message is present, clear all messages
        setMessages([]);
        return;
      } else {
        // Otherwise reset all
        resetSession && resetSession({});
        return;
      }
    }
  };

  return (
    <Formik
      initialValues={{ message: "" }}
      enableReinitialize
      onSubmit={(values, helpers) => {
        handleSubmit(values);
        helpers.resetForm();
      }}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValidating,
        resetForm,
        ...restProps
      }: any) => (
        <div className="ai-chat-interface">
          <div className="chat-header">
            <div className="header-content">
              <$Avatar icon={<RobotOutlined />} className="ai-avatar" />
              <div className="header-text">
                <$Typography className="ai-title">
                  {t("US.COMMON:UNIQ.UNIQ_ASSISTANT")}
                </$Typography>
              </div>
              <div className="header-actions">
                {type == URLType.CASE && (
                  <SettingsPopover messages={messages}>
                    <$Dropdown
                      menu={{
                        items: [
                          {
                            key: "default-prompt",
                            label: t("US.COMMON:UNIQ.DEFAULT_PROMPT"),
                            icon: <RobotOutlined />,
                            onClick: handleSettingsPopover,
                          },
                          {
                            key: "info",
                            label: t("US.COMMON:UNIQ.INFO"),
                            icon: <InfoCircleOutlined />,
                            onClick: handleShowInfo,
                          },
                        ],
                      }}
                      trigger={["click"]}
                      placement="bottomRight"
                    >
                      <$Button
                        type="text"
                        icon={<SettingOutlined />}
                        className="info-button"
                        title="Settings"
                        size="small"
                      ></$Button>
                    </$Dropdown>
                  </SettingsPopover>
                )}
                {type != URLType.CASE && (
                  <$Button
                    type="text"
                    icon={<InfoCircleOutlined />}
                    className="info-button"
                    title="Info"
                    size="small"
                    onClick={handleShowInfo}
                  />
                )}
                <$Button
                  type="text"
                  icon={<ReloadOutlined />}
                  onClick={handleStartOver}
                  className="new-chat-button"
                  title="Start New Chat"
                  size="small"
                  loading={sessionReset.isLoading}
                  disabled={messages.length === 0 || sessionReset.isLoading}
                >
                  {t("US.COMMON:UNIQ.START_OVER")}
                </$Button>
              </div>
            </div>
          </div>

          <div className="chat-messages">
            {messages.length === 0 && type != URLType.CASE ? (
              <WelcomeScreen />
            ) : (
              groupMessagesByDate(messages).map((item) => {
                if (item.type === "date") {
                  return <DateSeparator key={item.key} date={item.data} />;
                } else {
                  const message = item.data as UniQChatMessage;
                  return (
                    <div key={item.key} id={`msg-${message.id}`}>
                      <ChatBubble
                        key={item.key}
                        id={message.id}
                        responseType={message.responseType}
                        responseContent={message.responseContent}
                        links={message.links}
                        timestamp={message.timestamp}
                        isUser={message.isUser}
                        isError={message.isError}
                        attachments={message.attachments}
                        fullWidth={message.id.includes("case-sum-")}
                      />
                    </div>
                  );
                }
              })
            )}
            {(newMessage.isLoading || isFetchingDefaultPrompt) && (
              <ChatBubble
                id="loading"
                responseType="TEXT"
                responseContent=""
                links={[]}
                timestamp={new Date()}
                isUser={false}
                isLoading={true}
                loadingStatus={loadingStatus}
              />
            )}
          </div>

          <MessageInput
            caseType={type}
            value={values.message}
            onSubmit={handleSubmit}
            onClickRefreshSummary={handleRefreshSummary}
            disabled={newMessage.isLoading || isFetchingDefaultPrompt}
          />

          <div className="chat-disclaimer">
            <$Typography className="disclaimer-text">
              {t("US.COMMON:UNIQ.ACCURACY_DISCLAIMER")}
            </$Typography>
          </div>
        </div>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { uniQ } = state;
  const { isFetching, data } = uniQ.defaultPrompt;
  return {
    newMessage: uniQ.newMessage,
    messages: uniQ.messages,
    isFetchingDefaultPrompt: isFetching,
    defaultPromptData: data,
    sessionReset: uniQ.sessionReset,
  };
};

const mapDispatchToProps = {
  sendMessage: uniQActions.message.save,
  resetMessage: uniQActions.message.reset,
  clearMessages: uniQActions.messages.reset,
  openSettings: uniQActions.defaultPrompt.handlePopover,
  getDefaultPrompt: uniQActions.defaultPromptData.get,
  resetDefaultPromptData: uniQActions.defaultPromptData.reset,
  resetSession: uniQActions.session.reset,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(Assistant);
